import React, { useRef, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Column from '@amzn/meridian/column'
import { Telemetry } from '@amzn/ihm-telemetry-js-lib'
import { CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { getLastMigratedTool, getSelectedTool } from '@atredux/selectors/toolSelector'
import { IFRAME_PARENT_CONTAINER_HEIGHT, IFRAME_HEIGHT } from '@utils/ui-config'
import { getToolUrl, VIEW_ONLY_TOOLS_FLAG } from '@src/services/ToolMenuConfigService'
import { useFeatureFlag } from '@services/featureFlags'
import { getStoreId } from '@src/utils/EnvironmentUtil'
import { Orchestrator } from '@src/orchestration/manager'
import AssetCloudFrontCookieService from '@services/AssetCloudFrontCookieService'

interface IIFrameControllerProps {
    hide?: boolean
}

const ATBASE_WELCOME_PAGE = 'Welcome'

const IFrameController = ({}: Readonly<IIFrameControllerProps>): React.ReactElement => {
    const selectedTool = useSelector(getSelectedTool)
    const tool = useSelector(getLastMigratedTool)
    // getUrl() reads VIEW_ONLY_TOOLS_FLAG (viewOnly param, launch store); re-render when it loads.
    useFeatureFlag(VIEW_ONLY_TOOLS_FLAG)
    const startTimeRef = useRef<number>(0)
    const [cookiesReady, setCookiesReady] = useState(false)
    if (Orchestrator.instance.isTelemEnabled()) {
        Orchestrator.instance.workflow(tool.toolName)
        startTimeRef.current = new Date().getTime()
    } else {
        console.log('ATBase: Telemetry Disabled')
    }

    // Emit ToolLoaded metric each time a tool iframe is rendered
    useEffect(() => {
        if (tool.displayName !== ATBASE_WELCOME_PAGE) {
            const storeId = getStoreId()
            new CountMetric(storeId || undefined, 'ToolLoaded', 'JWOStoreManagementPlatform', tool.toolName || tool.displayName).count(1)
        }
    }, [tool.toolName, tool.displayName])

    // #3 (P494833843) fix: wait for CloudFront signed cookies before loading the /assets/* tool
    // iframe. Provisioning fires AssetCloudFrontCookieService.init() non-blocking (Provision/index.tsx),
    // so the CloudFront-Policy/Signature/Key-Pair-Id cookies may not be set yet when the first tool
    // renders -> CloudFront returns the XML "MissingKey" error (a manual refresh then works because
    // the cookies exist on the 2nd load). Gate the iframe src on cookie readiness, mirroring
    // WebPortalPage. init() is idempotent (skips when cookies are still valid); fail-open on error
    // so a cookie-fetch failure never blocks the tool from ever loading.
    useEffect(() => {
        AssetCloudFrontCookieService.init()
            .then(() => setCookiesReady(true))
            .catch(() => setCookiesReady(true))
    }, [])

    // Iframe visibility is controlled by the Column height prop below:
    // showMigrated=true → full height, showMigrated=false → 0vh.
    // No manual display:none override needed.

    const getUrl = () => {
        // Get base URL from tool registry.
        // Always pass tool.toolName (the assetId) as the toolId so a local dev
        // override keyed by assetId (local.dev.config.ts toolOverrides) is matched
        // for ALL tools — including the Cloudscape portal nav items (Team, Settings)
        // which are not in HIDDEN_TOOLS. getToolUrl only consults the registry by
        // toolId for HIDDEN_TOOLS, so passing it for other tools is otherwise inert.
        let baseUrl: string = getToolUrl(tool.displayName, tool.toolName)

        // Handle null baseUrl
        if (!baseUrl) {
            console.warn('JWOStoreManagementPlatform IFrameController: baseUrl is null, returning empty string')
            return ''
        }

        /**
         * Strip any existing hash fragment from baseUrl
         * getToolUrl() may include a hash from Redux state, but we always use tool.hashPath if provided
         */
        const hashIndex = baseUrl.indexOf('#')
        if (hashIndex !== -1) {
            baseUrl = baseUrl.substring(0, hashIndex)
        }

        /**
         * Append tool.hashPath for deep linking to specific pages within the tool
         * Example: When clicking "Solve" in notifications, hashPath = "/validate-fixture?fixtureId=X"
         * This enables navigation directly to the fixture validation page instead of the tool homepage
         * See: IndividualNotificationItem.tsx which sets hashPath when creating the tool object
         */
        if (tool.hashPath && typeof tool.hashPath === 'string' && tool.hashPath.length > 0) {
            /**
             * Prepend '#' to ensure proper hash fragment format for client-side routing
             * Correct format: .../index.html?params#/validate-fixture?fixtureId=X
             */
            const hashFragment = tool.hashPath.startsWith('#') ? tool.hashPath : '#' + tool.hashPath
            return baseUrl + hashFragment
        }

        return baseUrl
    }

    return (
        <Column className="iframe-container" height={
            selectedTool.showMigrated ? IFRAME_PARENT_CONTAINER_HEIGHT : '0vh'
        } heights={['fill']}>
            <iframe
                key = {Math.random()} // render a frame with a unique key always
                aria-label={tool.toolName}
                title={tool.toolName}
                src={tool.displayName === ATBASE_WELCOME_PAGE || !cookiesReady ? '' : getUrl()}
                role="presentation"
                style={{ display: 'inline', border: 'none', height: IFRAME_HEIGHT }}
                className={'meridian-tool-frame'}
                onLoad={(e) => {
                    if (Orchestrator.instance.isTelemEnabled()) {
                        const loadTime = new Date().getTime() - startTimeRef.current
                        const eventData: Record<string, unknown> = {
                            toolId : tool.toolName,
                            latency : loadTime,
                        }
                        Telemetry.emit('toolSwitchLatency', eventData)
                    }
                }}
            />
        </Column>
    )
}

export default IFrameController
